import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Unstable_Grid2';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import cloudApi from '../../../app/api/cloudApi';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Icon } from '@iconify/react';
import { useAvailableActions } from '../../../hooks/useAvailableActions';
import {
  formatFaceScannerModel,
  formatFaceScannerDeviceLabel,
} from '../../../utils/format-face-scanner-model';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';

const QUEUED_REFRESH_DELAY_MS = 3000;
const PENDING_HUB_FALLBACK_MS = 15000;
const EVENTS_HUB_ENABLED = process.env.REACT_APP_EVENTS_HUB_ENABLED !== 'false';
const EVENTS_HUB_URL = process.env.REACT_APP_EVENTS_HUB_URL?.trim() ?? '';

const isEventsHubConfigured = () => EVENTS_HUB_ENABLED && EVENTS_HUB_URL.length > 0;

const getGateInDeviceIds = (gate) => {
  if (gate.inDeviceIds?.length) return gate.inDeviceIds.map(Number);
  if (gate.InDeviceIds?.length) return gate.InDeviceIds.map(Number);
  if (gate.inDeviceId != null && gate.inDeviceId !== '') return [Number(gate.inDeviceId)];
  if (gate.InDeviceId != null && gate.InDeviceId !== '') return [Number(gate.InDeviceId)];
  return [];
};

const getGateOutDeviceIds = (gate) => {
  if (gate.outDeviceIds?.length) return gate.outDeviceIds.map(Number);
  if (gate.OutDeviceIds?.length) return gate.OutDeviceIds.map(Number);
  if (gate.outDeviceId != null && gate.outDeviceId !== '') return [Number(gate.outDeviceId)];
  if (gate.OutDeviceId != null && gate.OutDeviceId !== '') return [Number(gate.OutDeviceId)];
  return [];
};

const deviceIdListsEqual = (left, right) => {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].map(Number).sort((a, b) => a - b);
  const rightSorted = [...right].map(Number).sort((a, b) => a - b);
  return leftSorted.every((id, index) => id === rightSorted[index]);
};

/** Multi-select edit: the new arrays are taken directly from the dialog's selections. */
const buildEditDeviceIdLists = (original, inDeviceIds, outDeviceIds) => {
  const normalizedInDeviceIds = inDeviceIds.map(Number);
  const normalizedOutDeviceIds = outDeviceIds.map(Number);

  return {
    inDeviceIds: normalizedInDeviceIds,
    outDeviceIds: normalizedOutDeviceIds,
    devicesChanged:
      !deviceIdListsEqual(normalizedInDeviceIds, original.inDeviceIds) ||
      !deviceIdListsEqual(normalizedOutDeviceIds, original.outDeviceIds),
  };
};

const createCorrelationId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalizeGateId = (gateId) => String(gateId).toLowerCase();

const BOX_SYNC_CHIP = {
  Pending: { label: 'Syncing to box', color: 'info' },
  Failed: { label: 'Box sync failed', color: 'error' },
  PendingDelete: { label: 'Deleting from box', color: 'warning' },
  DeleteFailed: { label: 'Box delete failed', color: 'error' },
};

function BoxSyncStatusChip({ entity }) {
  const status = entity?.boxSyncStatus || entity?.BoxSyncStatus;
  const config = BOX_SYNC_CHIP[status];
  if (!config) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={config.color}
      label={config.label}
      title={entity.boxSyncFailureCode || entity.BoxSyncFailureCode || undefined}
    />
  );
}

export default function FaceScannerGatesPanel({ site }) {
  const siteId = site?.id;
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((state) => state.app.account);
  const { canEditSites } = useAvailableActions();

  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gateGroups, setGateGroups] = useState([]);
  const [devices, setDevices] = useState([]);

  const [groupDialog, setGroupDialog] = useState({ open: false, mode: 'create', id: null, name: '' });
  const [gateDialog, setGateDialog] = useState({
    open: false,
    mode: 'create',
    id: null,
    name: '',
    gateGroupId: '',
    inDeviceIds: [],
    outDeviceIds: [],
    original: null,
  });
  const [apbDialog, setApbDialog] = useState({
    open: false,
    groupId: null,
    groupName: '',
    hardMode: true,
    resetDurationSeconds: 0,
    isEnabled: false,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    id: null,
    name: '',
  });
  const [saving, setSaving] = useState(false);
  const pendingGateOperationsRef = useRef(new Map());
  const pendingDeleteGateIdsRef = useRef(new Set());
  const hubConnectedRef = useRef(false);
  const hubSubscribedRef = useRef(false);
  const hubConnectingRef = useRef(false);
  const pendingHubFallbackTimerRef = useRef(null);
  const refreshAfterMutationRef = useRef(async () => {});
  const schedulePendingHubFallbackRef = useRef(() => {});
  const runPendingHubFallbackRef = useRef(() => {});

  const deviceMap = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => map.set(d.deviceID, d));
    return map;
  }, [devices]);

  const resolveDeviceLabel = useCallback(
    (deviceId) => {
      const device = deviceMap.get(deviceId);
      if (!device) return `Device ${deviceId}`;
      return formatFaceScannerDeviceLabel(device);
    },
    [deviceMap]
  );

  const resolveDeviceLabels = useCallback(
    (deviceIds) => {
      if (!deviceIds?.length) return '—';
      return deviceIds.map((id) => resolveDeviceLabel(id)).join(', ');
    },
    [resolveDeviceLabel]
  );

  const registerPendingGateOperation = useCallback((correlationId, operation) => {
    if (correlationId) {
      pendingGateOperationsRef.current.set(correlationId, operation);
    }
  }, []);

  const hasPendingGateOperations = useCallback(
    () =>
      pendingGateOperationsRef.current.size > 0 || pendingDeleteGateIdsRef.current.size > 0,
    []
  );

  const clearPendingCorrelationIds = useCallback((correlationIds) => {
    correlationIds.forEach((id) => pendingGateOperationsRef.current.delete(id));
  }, []);

  const clearPendingHubFallback = useCallback(() => {
    if (pendingHubFallbackTimerRef.current != null) {
      clearTimeout(pendingHubFallbackTimerRef.current);
      pendingHubFallbackTimerRef.current = null;
    }
  }, []);

  const resetPendingGateOperationState = useCallback(() => {
    pendingGateOperationsRef.current.clear();
    pendingDeleteGateIdsRef.current.clear();
    clearPendingHubFallback();
  }, [clearPendingHubFallback]);

  const loadGateGroups = useCallback(async () => {
    if (!auth.user?.access_token || !account?.id || !siteId) return;

    const response = await cloudApi.getFaceScannerGateGroups(
      auth.user.access_token,
      siteId,
      account.id
    );
    setGateGroups(response.data || []);
  }, [auth.user?.access_token, account?.id, siteId]);

  const loadDevices = useCallback(async () => {
    if (!auth.user?.access_token || !account?.id || !siteId) return;

    const response = await cloudApi.getFaceScannerConnectedDevices(
      auth.user.access_token,
      siteId,
      account.id
    );
    setDevices(response.data || []);
  }, [auth.user?.access_token, account?.id, siteId]);

  const refreshData = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      try {
        await Promise.all([loadGateGroups(), loadDevices()]);
      } catch (err) {
        const message = err.response?.data || err.message || 'Failed to refresh gate data';
        enqueueSnackbar(typeof message === 'string' ? message : 'Failed to refresh gate data', {
          variant: 'error',
        });
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [loadGateGroups, loadDevices, enqueueSnackbar]
  );

  const refreshAfterMutation = useCallback(async () => {
    setTimeout(() => refreshData(false), QUEUED_REFRESH_DELAY_MS);
    await refreshData(false);
  }, [refreshData]);

  refreshAfterMutationRef.current = refreshAfterMutation;

  const shouldAwaitGateOperationHubEvent = useCallback(() => isEventsHubConfigured(), []);

  const runPendingHubFallback = useCallback(
    (reason = 'timed out waiting for status') => {
      clearPendingHubFallback();
      if (!hasPendingGateOperations()) return;

      const pendingOperations = [...pendingGateOperationsRef.current.values()];
      if (pendingDeleteGateIdsRef.current.size > 0) {
        pendingDeleteGateIdsRef.current.forEach(() => pendingOperations.push('DeleteGate'));
      }

      pendingGateOperationsRef.current.clear();
      pendingDeleteGateIdsRef.current.clear();

      if (pendingOperations.length > 0) {
        const summary =
          pendingOperations.length === 1
            ? pendingOperations[0]
            : `${pendingOperations.length} gate operations`;

        enqueueSnackbar(
          `${summary}: no result received (${reason}). Refreshing gate list.`,
          { variant: 'warning' }
        );
      }

      refreshAfterMutationRef.current();
    },
    [clearPendingHubFallback, hasPendingGateOperations, enqueueSnackbar]
  );

  const schedulePendingHubFallback = useCallback(() => {
    if (!isEventsHubConfigured() || !hasPendingGateOperations()) return;

    clearPendingHubFallback();
    pendingHubFallbackTimerRef.current = setTimeout(() => {
      pendingHubFallbackTimerRef.current = null;
      runPendingHubFallback();
    }, PENDING_HUB_FALLBACK_MS);
  }, [clearPendingHubFallback, hasPendingGateOperations, runPendingHubFallback]);

  const notePendingGateOperationSubmitted = useCallback(() => {
    if (isEventsHubConfigured()) {
      schedulePendingHubFallback();
    }
  }, [schedulePendingHubFallback]);

  schedulePendingHubFallbackRef.current = schedulePendingHubFallback;
  runPendingHubFallbackRef.current = runPendingHubFallback;

  const refreshIfNotAwaitingHubEvent = useCallback(async () => {
    if (isEventsHubConfigured()) return;

    pendingGateOperationsRef.current.clear();
    pendingDeleteGateIdsRef.current.clear();
    clearPendingHubFallback();
    await refreshAfterMutation();
  }, [clearPendingHubFallback, refreshAfterMutation]);

  const handleGateOperationResult = useCallback(
    (message) => {
      if (!message || String(message.siteId).toLowerCase() !== String(siteId).toLowerCase()) return;

      let isOwnOperation = false;

      if (message.correlationId && pendingGateOperationsRef.current.has(message.correlationId)) {
        pendingGateOperationsRef.current.delete(message.correlationId);
        isOwnOperation = true;
      } else if (
        message.operation === 'DeleteGate' &&
        pendingDeleteGateIdsRef.current.has(normalizeGateId(message.gateId))
      ) {
        pendingDeleteGateIdsRef.current.delete(normalizeGateId(message.gateId));
        isOwnOperation = true;
      }

      if (!isOwnOperation) return;

      if (message.success) {
        enqueueSnackbar(`${message.operation} completed`, { variant: 'success' });
      } else {
        enqueueSnackbar(
          message.resultMessage || message.failureCode || `${message.operation} failed`,
          { variant: 'error' }
        );
      }
      refreshAfterMutation();

      if (hasPendingGateOperations()) {
        schedulePendingHubFallback();
      } else {
        clearPendingHubFallback();
      }
    },
    [siteId, enqueueSnackbar, refreshAfterMutation, hasPendingGateOperations, schedulePendingHubFallback, clearPendingHubFallback]
  );

  useEffect(() => {
    resetPendingGateOperationState();
  }, [siteId, resetPendingGateOperationState]);

  useEffect(() => {
    if (!isEventsHubConfigured() || !siteId || !auth.user?.access_token) {
      hubConnectedRef.current = false;
      hubSubscribedRef.current = false;
      hubConnectingRef.current = false;
      return undefined;
    }

    let cancelled = false;

    const markHubNotReady = () => {
      hubConnectingRef.current = false;
      hubConnectedRef.current = false;
      hubSubscribedRef.current = false;
    };

    const connection = new HubConnectionBuilder()
      .withUrl(EVENTS_HUB_URL, {
        accessTokenFactory: () => auth.user.access_token,
        transport: HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    const subscribeForCurrentSite = () => {
      hubConnectingRef.current = true;
      return connection
        .invoke('SubscribeForSite', siteId)
        .then(() => {
          if (cancelled) return;
          hubConnectingRef.current = false;
          hubSubscribedRef.current = true;
          if (
            pendingGateOperationsRef.current.size > 0 ||
            pendingDeleteGateIdsRef.current.size > 0
          ) {
            schedulePendingHubFallbackRef.current();
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Error subscribing to events hub for site:', err);
          }
          markHubNotReady();
          throw err;
        });
    };

    const handleHubUnavailable = () => {
      markHubNotReady();
      if (cancelled) return;

      if (
        pendingGateOperationsRef.current.size > 0 ||
        pendingDeleteGateIdsRef.current.size > 0
      ) {
        schedulePendingHubFallbackRef.current();
      }
    };

    hubConnectingRef.current = true;
    if (
      pendingGateOperationsRef.current.size > 0 ||
      pendingDeleteGateIdsRef.current.size > 0
    ) {
      schedulePendingHubFallbackRef.current();
    }

    connection
      .start()
      .then(() => {
        if (cancelled) return Promise.reject(new Error('Hub connection cancelled'));
        hubConnectedRef.current = true;
        return subscribeForCurrentSite();
      })
      .catch((err) => {
        if (!cancelled && err?.message !== 'Hub connection cancelled') {
          console.error('Error connecting to events hub:', err);
        }
        handleHubUnavailable();
      });

    connection.onclose(() => {
      handleHubUnavailable();
    });

    connection.onreconnecting(() => {
      hubSubscribedRef.current = false;
      hubConnectingRef.current = true;
      if (cancelled) return;

      if (
        pendingGateOperationsRef.current.size > 0 ||
        pendingDeleteGateIdsRef.current.size > 0
      ) {
        schedulePendingHubFallbackRef.current();
      }
    });

    connection.onreconnected(() => {
      if (cancelled) return;

      hubConnectedRef.current = true;
      subscribeForCurrentSite().catch(() => handleHubUnavailable());
    });

    connection.on('GateOperationResultUpdated', handleGateOperationResult);

    return () => {
      cancelled = true;
      markHubNotReady();
      clearPendingHubFallback();
      connection.off('GateOperationResultUpdated', handleGateOperationResult);
      void connection.stop().catch((err) => {
        console.error('Error stopping events hub connection:', err);
      });
    };
  }, [siteId, auth.user?.access_token, handleGateOperationResult, clearPendingHubFallback]);

  useEffect(() => {
    const initializePage = async () => {
      setError(null);
      setPageLoading(true);

      if (!auth.isAuthenticated || !auth.user) {
        setError('Please log in to access this page');
        setPageLoading(false);
        return;
      }

      if (!siteId) {
        setError('Site ID is required');
        setPageLoading(false);
        return;
      }

      try {
        await Promise.all([loadGateGroups(), loadDevices()]);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load page');
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
  }, [auth.isAuthenticated, auth.user, siteId, loadGateGroups, loadDevices]);

  const openCreateGroupDialog = () => {
    setGroupDialog({ open: true, mode: 'create', id: null, name: '' });
  };

  const openEditGroupDialog = (group) => {
    setGroupDialog({ open: true, mode: 'edit', id: group.id, name: group.name || '' });
  };

  const openCreateGateDialog = (groupId) => {
    setGateDialog({
      open: true,
      mode: 'create',
      id: null,
      name: '',
      gateGroupId: groupId,
      inDeviceIds: [],
      outDeviceIds: [],
      original: null,
    });
  };

  const openEditGateDialog = (gate) => {
    const inDeviceIds = getGateInDeviceIds(gate);
    const outDeviceIds = getGateOutDeviceIds(gate);

    setGateDialog({
      open: true,
      mode: 'edit',
      id: gate.id,
      name: gate.name || '',
      gateGroupId: gate.gateGroupId || '',
      inDeviceIds,
      outDeviceIds,
      original: {
        name: gate.name || '',
        gateGroupId: gate.gateGroupId || '',
        inDeviceIds,
        outDeviceIds,
      },
    });
  };

  const openApbDialog = (group) => {
    setApbDialog({
      open: true,
      groupId: group.id,
      groupName: group.name || '',
      hardMode: group.apbHardMode ?? true,
      resetDurationSeconds: group.apbResetDurationSeconds ?? 0,
      isEnabled: group.isApbEnabled ?? false,
    });
  };

  const handleSaveGroup = async () => {
    if (!groupDialog.name?.trim()) {
      enqueueSnackbar('Group name is required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const token = auth.user.access_token;
      if (groupDialog.mode === 'create') {
        await cloudApi.createFaceScannerGateGroup(token, siteId, account.id, groupDialog.name.trim());
        enqueueSnackbar('Gate group created', { variant: 'success' });
      } else {
        await cloudApi.updateFaceScannerGateGroup(
          token,
          siteId,
          account.id,
          groupDialog.id,
          groupDialog.name.trim()
        );
        enqueueSnackbar('Gate group updated', { variant: 'success' });
      }
      setGroupDialog({ open: false, mode: 'create', id: null, name: '' });
      await refreshAfterMutation();
    } catch (err) {
      const message = err.response?.data || err.message || 'Failed to save gate group';
      enqueueSnackbar(typeof message === 'string' ? message : 'Failed to save gate group', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGate = async () => {
    if (!gateDialog.name?.trim()) {
      enqueueSnackbar('Gate name is required', { variant: 'warning' });
      return;
    }
    if (!gateDialog.gateGroupId) {
      enqueueSnackbar('Gate group is required', { variant: 'warning' });
      return;
    }
    if (gateDialog.inDeviceIds.length === 0 || gateDialog.outDeviceIds.length === 0) {
      enqueueSnackbar('At least one Gate-In and one Gate-Out device are required', { variant: 'warning' });
      return;
    }
    const inOutOverlap = gateDialog.inDeviceIds.some((id) =>
      gateDialog.outDeviceIds.map(Number).includes(Number(id))
    );
    if (inOutOverlap) {
      enqueueSnackbar('A device cannot be assigned as both Gate-In and Gate-Out on the same gate', {
        variant: 'warning',
      });
      return;
    }

    setSaving(true);
    const submittedCorrelationIds = [];

    try {
      const token = auth.user.access_token;
      const name = gateDialog.name.trim();
      const gateGroupId = gateDialog.gateGroupId;

      if (gateDialog.mode === 'create') {
        const inDeviceIds = gateDialog.inDeviceIds.map(Number);
        const outDeviceIds = gateDialog.outDeviceIds.map(Number);
        const correlationId = createCorrelationId();
        submittedCorrelationIds.push(correlationId);
        registerPendingGateOperation(correlationId, 'CreateGate');

        await cloudApi.createFaceScannerGate(token, siteId, account.id, {
          name,
          inDeviceIds,
          outDeviceIds,
          gateGroupId,
          correlationId,
        });
        enqueueSnackbar('Gate creation submitted', { variant: 'info' });
      } else {
        const original = gateDialog.original;
        const editRequests = [];

        if (original && name !== original.name) {
          const correlationId = createCorrelationId();
          submittedCorrelationIds.push(correlationId);
          registerPendingGateOperation(correlationId, 'RenameGate');
          editRequests.push({
            correlationId,
            operation: 'RenameGate',
            promise: cloudApi.renameFaceScannerGate(token, siteId, account.id, {
              id: gateDialog.id,
              name,
              correlationId,
            }),
          });
        }

        if (original && String(gateGroupId) !== String(original.gateGroupId)) {
          const correlationId = createCorrelationId();
          submittedCorrelationIds.push(correlationId);
          registerPendingGateOperation(correlationId, 'MoveGate');
          editRequests.push({
            correlationId,
            operation: 'MoveGate',
            promise: cloudApi.moveFaceScannerGate(token, siteId, account.id, {
              id: gateDialog.id,
              gateGroupId,
              correlationId,
            }),
          });
        }

        if (original) {
          const { inDeviceIds, outDeviceIds, devicesChanged } = buildEditDeviceIdLists(
            original,
            gateDialog.inDeviceIds,
            gateDialog.outDeviceIds
          );

          if (devicesChanged) {
            const correlationId = createCorrelationId();
            submittedCorrelationIds.push(correlationId);
            registerPendingGateOperation(correlationId, 'ReplaceGateDevices');
            editRequests.push({
              correlationId,
              operation: 'ReplaceGateDevices',
              promise: cloudApi.replaceFaceScannerGateDevices(token, siteId, account.id, {
                id: gateDialog.id,
                inDeviceIds,
                outDeviceIds,
                correlationId,
              }),
            });
          }
        }

        if (editRequests.length === 0) {
          enqueueSnackbar('No changes to save', { variant: 'info' });
          setSaving(false);
          return;
        }

        const results = await Promise.allSettled(editRequests.map((request) => request.promise));
        const failedCorrelationIds = [];

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            failedCorrelationIds.push(editRequests[index].correlationId);
          }
        });

        if (failedCorrelationIds.length === editRequests.length) {
          clearPendingCorrelationIds(submittedCorrelationIds);
          const firstError = results.find((result) => result.status === 'rejected');
          throw firstError?.reason ?? new Error('Failed to save gate');
        }

        if (failedCorrelationIds.length > 0) {
          clearPendingCorrelationIds(failedCorrelationIds);
          if (!shouldAwaitGateOperationHubEvent()) {
            await refreshAfterMutation();
          }
          if (hasPendingGateOperations()) {
            notePendingGateOperationSubmitted();
          }

          const updatedOriginal = { ...original };
          results.forEach((result, index) => {
            if (result.status !== 'fulfilled') return;

            switch (editRequests[index].operation) {
              case 'RenameGate':
                updatedOriginal.name = name;
                break;
              case 'MoveGate':
                updatedOriginal.gateGroupId = gateGroupId;
                break;
              case 'ReplaceGateDevices': {
                const { inDeviceIds, outDeviceIds } = buildEditDeviceIdLists(
                  original,
                  gateDialog.inDeviceIds,
                  gateDialog.outDeviceIds
                );
                updatedOriginal.inDeviceIds = inDeviceIds;
                updatedOriginal.outDeviceIds = outDeviceIds;
                break;
              }
              default:
                break;
            }
          });

          setGateDialog((prev) => ({
            ...prev,
            original: updatedOriginal,
          }));

          enqueueSnackbar(
            `${failedCorrelationIds.length} of ${editRequests.length} gate updates failed`,
            { variant: 'warning' }
          );
          return;
        } else {
          enqueueSnackbar('Gate update submitted', { variant: 'info' });
        }
      }

      setGateDialog({
        open: false,
        mode: 'create',
        id: null,
        name: '',
        gateGroupId: '',
        inDeviceIds: [],
        outDeviceIds: [],
        original: null,
      });
      notePendingGateOperationSubmitted();
      await refreshIfNotAwaitingHubEvent();
    } catch (err) {
      clearPendingCorrelationIds(submittedCorrelationIds);
      if (hasPendingGateOperations()) {
        notePendingGateOperationSubmitted();
      } else {
        clearPendingHubFallback();
      }
      if (!shouldAwaitGateOperationHubEvent()) {
        await refreshAfterMutation();
      }
      const message = err.response?.data || err.message || 'Failed to save gate';
      enqueueSnackbar(typeof message === 'string' ? message : 'Failed to save gate', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApb = async () => {
    setSaving(true);
    try {
      const token = auth.user.access_token;
      await cloudApi.configureFaceScannerApbZone(
        token,
        siteId,
        account.id,
        apbDialog.groupId,
        apbDialog.hardMode,
        apbDialog.resetDurationSeconds
      );
      enqueueSnackbar('APB zone configured', { variant: 'success' });
      setApbDialog({
        open: false,
        groupId: null,
        groupName: '',
        hardMode: true,
        resetDurationSeconds: 0,
        isEnabled: false,
      });
      await refreshAfterMutation();
    } catch (err) {
      const message = err.response?.data || err.message || 'Failed to configure APB zone';
      enqueueSnackbar(typeof message === 'string' ? message : 'Failed to configure APB zone', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApb = async (groupId) => {
    setSaving(true);
    try {
      await cloudApi.removeFaceScannerApbZone(auth.user.access_token, siteId, account.id, groupId);
      enqueueSnackbar('APB zone removed', { variant: 'success' });
      await refreshAfterMutation();
    } catch (err) {
      const message = err.response?.data || err.message || 'Failed to remove APB zone';
      enqueueSnackbar(typeof message === 'string' ? message : 'Failed to remove APB zone', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const deleteType = deleteDialog.type;
    const deleteId = deleteDialog.id;

    setSaving(true);
    try {
      const token = auth.user.access_token;
      if (deleteType === 'group') {
        await cloudApi.deleteFaceScannerGateGroup(token, siteId, account.id, deleteId);
        enqueueSnackbar('Gate group deleted', { variant: 'success' });
      } else if (deleteType === 'gate') {
        pendingDeleteGateIdsRef.current.add(normalizeGateId(deleteId));
        await cloudApi.deleteFaceScannerGate(token, siteId, account.id, deleteId);
        enqueueSnackbar('Gate deletion submitted', { variant: 'info' });
      }
      setDeleteDialog({ open: false, type: null, id: null, name: '' });
      if (deleteType === 'group') {
        await refreshAfterMutation();
      } else if (deleteType === 'gate') {
        notePendingGateOperationSubmitted();
        await refreshIfNotAwaitingHubEvent();
      }
    } catch (err) {
      if (deleteType === 'gate') {
        pendingDeleteGateIdsRef.current.delete(normalizeGateId(deleteId));
      }
      if (!hasPendingGateOperations()) {
        clearPendingHubFallback();
      }
      const message = err.response?.data || err.message || 'Failed to delete';
      enqueueSnackbar(typeof message === 'string' ? message : 'Failed to delete', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Gate Groups</Typography>
        <Stack direction="row" spacing={2}>
          {canEditSites && (
            <Button
              variant="contained"
              startIcon={<Icon icon="solar:add-circle-bold" />}
              onClick={openCreateGroupDialog}
            >
              Add Gate Group
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Icon icon="solar:refresh-bold" />}
            onClick={() => refreshData()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
          <Grid xs={12}>
            <Card>
              <CardHeader
                title="Gate Groups"
                subheader="Logical gates pair Gate-In and Gate-Out FaceScanner devices for time & attendance tracking."
              />
              <CardContent>
                {gateGroups.length === 0 ? (
                  <Alert severity="info">
                    No gate groups configured.{' '}
                    {canEditSites && 'Click "Add Gate Group" to create one.'}
                  </Alert>
                ) : (
                  gateGroups.map((group) => (
                    <Accordion key={group.id} defaultExpanded sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<Icon icon="solar:alt-arrow-down-bold" />}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ width: '100%', pr: 2 }}
                        >
                          <Typography variant="subtitle1" fontWeight="medium">
                            {group.name || 'Unnamed group'}
                          </Typography>
                          <BoxSyncStatusChip entity={group} />
                          <Chip
                            size="small"
                            label={group.isApbEnabled ? 'APB enabled' : 'APB disabled'}
                            color={group.isApbEnabled ? 'success' : 'default'}
                            variant="outlined"
                          />
                          {group.isApbEnabled && (
                            <Chip
                              size="small"
                              label={group.apbHardMode ? 'Hard mode' : 'Soft mode'}
                              variant="outlined"
                            />
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {canEditSites && (
                            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                              <IconButton size="small" onClick={() => openEditGroupDialog(group)}>
                                <Icon icon="solar:pen-bold" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setDeleteDialog({
                                    open: true,
                                    type: 'group',
                                    id: group.id,
                                    name: group.name,
                                  })
                                }
                              >
                                <Icon icon="solar:trash-bin-trash-bold" />
                              </IconButton>
                            </Stack>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {canEditSites && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Icon icon="solar:add-circle-bold" />}
                                  onClick={() => openCreateGateDialog(group.id)}
                                >
                                  Add Gate
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Icon icon="solar:shield-check-bold" />}
                                  onClick={() => openApbDialog(group)}
                                >
                                  {group.isApbEnabled ? 'Edit APB' : 'Configure APB'}
                                </Button>
                                {group.isApbEnabled && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => handleRemoveApb(group.id)}
                                    disabled={saving}
                                  >
                                    Remove APB
                                  </Button>
                                )}
                              </>
                            )}
                          </Stack>

                          {group.gates?.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Gate Name</TableCell>
                                    <TableCell>Gate-In Device</TableCell>
                                    <TableCell>Gate-Out Device</TableCell>
                                    {canEditSites && <TableCell align="right">Actions</TableCell>}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.gates.map((gate) => (
                                    <TableRow key={gate.id} hover>
                                      <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <span>{gate.name || 'Unnamed'}</span>
                                          <BoxSyncStatusChip entity={gate} />
                                        </Stack>
                                      </TableCell>
                                      <TableCell>{resolveDeviceLabels(getGateInDeviceIds(gate))}</TableCell>
                                      <TableCell>{resolveDeviceLabels(getGateOutDeviceIds(gate))}</TableCell>
                                      {canEditSites && (
                                        <TableCell align="right">
                                          <IconButton size="small" onClick={() => openEditGateDialog(gate)}>
                                            <Icon icon="solar:pen-bold" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              setDeleteDialog({
                                                open: true,
                                                type: 'gate',
                                                id: gate.id,
                                                name: gate.name,
                                              })
                                            }
                                          >
                                            <Icon icon="solar:trash-bin-trash-bold" />
                                          </IconButton>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Alert severity="info" variant="outlined">
                              No gates in this group.
                              {canEditSites && ' Add a gate to assign Gate-In and Gate-Out devices.'}
                            </Alert>
                          )}

                          {group.isApbEnabled && (
                            <Box sx={{ pt: 1 }}>
                              <Divider sx={{ mb: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                APB reset duration:{' '}
                                {group.apbResetDurationSeconds > 0
                                  ? `${group.apbResetDurationSeconds}s`
                                  : 'Indefinite (until badge-out or manual reset)'}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>

          {devices.length > 0 && (
            <Grid xs={12}>
              <Card>
                <CardHeader title="Connected Devices" subheader="Available for Gate-In / Gate-Out assignment" />
                <CardContent>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Device ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell>IP Address</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {devices.map((device) => (
                          <TableRow key={device.deviceID} hover>
                            <TableCell>{device.deviceID}</TableCell>
                            <TableCell>{device.name || 'N/A'}</TableCell>
                            <TableCell>
                              {formatFaceScannerModel(device.modelName, device.type)}
                            </TableCell>
                            <TableCell>{device.ipAddress || 'N/A'}</TableCell>
                            <TableCell>{device.status || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

      {/* Gate Group Dialog */}
      <Dialog open={groupDialog.open} onClose={() => setGroupDialog({ ...groupDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{groupDialog.mode === 'create' ? 'Add Gate Group' : 'Edit Gate Group'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={groupDialog.name}
            onChange={(e) => setGroupDialog({ ...groupDialog, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialog({ ...groupDialog, open: false })}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gate Dialog */}
      <Dialog open={gateDialog.open} onClose={() => setGateDialog({ ...gateDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{gateDialog.mode === 'create' ? 'Add Gate' : 'Edit Gate'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Gate Name"
              fullWidth
              value={gateDialog.name}
              onChange={(e) => setGateDialog({ ...gateDialog, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Gate Group</InputLabel>
              <Select
                label="Gate Group"
                value={gateDialog.gateGroupId}
                onChange={(e) => setGateDialog({ ...gateDialog, gateGroupId: e.target.value })}
              >
                {gateGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={devices}
              value={devices.filter((d) => gateDialog.inDeviceIds.includes(d.deviceID))}
              onChange={(event, newValue) =>
                setGateDialog({ ...gateDialog, inDeviceIds: newValue.map((d) => d.deviceID) })
              }
              getOptionLabel={(d) => formatFaceScannerDeviceLabel(d)}
              isOptionEqualToValue={(option, value) => option.deviceID === value.deviceID}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={formatFaceScannerDeviceLabel(option)}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.deviceID}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Gate-In Device(s)" placeholder="Select device(s)" />
              )}
            />
            <Autocomplete
              multiple
              options={devices}
              value={devices.filter((d) => gateDialog.outDeviceIds.includes(d.deviceID))}
              onChange={(event, newValue) =>
                setGateDialog({ ...gateDialog, outDeviceIds: newValue.map((d) => d.deviceID) })
              }
              getOptionLabel={(d) => formatFaceScannerDeviceLabel(d)}
              isOptionEqualToValue={(option, value) => option.deviceID === value.deviceID}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={formatFaceScannerDeviceLabel(option)}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.deviceID}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Gate-Out Device(s)" placeholder="Select device(s)" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGateDialog({ ...gateDialog, open: false })}>Cancel</Button>
          <Button onClick={handleSaveGate} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* APB Dialog */}
      <Dialog open={apbDialog.open} onClose={() => setApbDialog({ ...apbDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Configure APB — {apbDialog.groupName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={apbDialog.hardMode}
                  onChange={(e) => setApbDialog({ ...apbDialog, hardMode: e.target.checked })}
                />
              }
              label="Hard mode (deny on APB violation)"
            />
            <TextField
              label="Reset duration (seconds)"
              type="number"
              fullWidth
              helperText="0 = indefinite until badge-out or manual reset. 86400 = 24-hour auto-reset."
              value={apbDialog.resetDurationSeconds}
              onChange={(e) =>
                setApbDialog({
                  ...apbDialog,
                  resetDurationSeconds: Math.max(0, Number(e.target.value) || 0),
                })
              }
              inputProps={{ min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApbDialog({ ...apbDialog, open: false })}>Cancel</Button>
          <Button onClick={handleSaveApb} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteDialog.type === 'group' ? 'gate group' : 'gate'}{' '}
            <strong>{deleteDialog.name || 'Unnamed'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
